import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, brandMilestones } from '@/mocks/testimonials';

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypot = formPayload.get('company_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setStatusMessage('消息已发送，我们会尽快回复您！');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setStatus('loading');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u3h0t3pcjqs2fcsl9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          company_alt: '',
        }).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('感谢您的留言，我们会尽快回复您！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setStatus('error');
          setStatusMessage('提交失败，请稍后重试。');
        } else {
          setStatus('error');
          setStatusMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        />
        <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
      >
        {status === 'loading' ? '发送中...' : '发送留言'}
      </button>
      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="anti-spam-field" />
      {status === 'success' && <p className="text-sm text-green-600 text-center">{statusMessage}</p>}
      {status === 'error' && <p className="text-sm text-red-500 text-center">{statusMessage}</p>}
    </form>
  );
}

export default function About() {
  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-1/2">
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cozy%20butter%20shop%20interior%20with%20wooden%20shelves%20displaying%20premium%20butter%20products%2C%20cream%20colored%20walls%2C%20soft%20natural%20lighting%2C%20rustic%20elegant%20style%2C%20high%20detail%2C%20inviting%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="在线黄油品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-5">
                  关于在线黄油
                </h1>
                <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">在线黄油</strong>成立于2018年，是国内领先的专注黄油品类的垂直电商平台。我们致力于为中国消费者提供来自全球优质产区的正品黄油，让每一位烘焙爱好者和美食达人都能轻松选购到心仪的黄油产品。
                  </p>
                  <p>
                    我们深知，一块好的黄油是美味料理的灵魂。从法国诺曼底的AOP认证发酵黄油，到新西兰纯净牧场的无盐动物黄油，从丹麦有机农场的有机黄油，到比利时专业烘焙黄油片——在线黄油平台汇聚了来自全球8大产区的50+款精品黄油。
                  </p>
                  <p>
                    品质是我们的生命线。每一批上架的黄油产品都经过严格的供应链审核和品质检测。我们建立了覆盖全国30+城市的<strong className="text-foreground-900">冷链物流体系</strong>，确保每一块黄油从仓库到您手中都保持最佳状态。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                品牌发展历程
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                从创立到行业领军，每一步都踏实前行
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="flex flex-col gap-8">
                {brandMilestones.map((item, index) => (
                  <div key={item.year} className={`relative flex items-start gap-6 md:gap-10 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                    <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <span className="inline-block text-sm font-bold text-primary-500 mb-1">{item.year}</span>
                      <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-500">{item.description}</p>
                    </div>
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                用户真实评价
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                来自全国各地的黄油爱好者对在线黄油的真实反馈
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-background-100 flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.location} · {t.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">{t.content}</p>
                  <p className="text-xs text-foreground-400">购买产品：{t.product}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="w-full lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-500 mb-8 leading-relaxed">
                  如果您有任何关于产品选购、配送服务或售后问题的疑问，欢迎随时联系我们。我们的客服团队将竭诚为您服务。
                </p>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-phone-line"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">客服热线</p>
                      <p className="text-sm text-foreground-500">400-123-4567</p>
                      <p className="text-xs text-foreground-400">周一至周日 9:00-21:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-mail-line"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">电子邮箱</p>
                      <p className="text-sm text-foreground-500">service@onlinebutter.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-map-pin-line"></i>
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">公司地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座1508室</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-1/2">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">在线留言</h3>
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ on About page */}
        <section id="faq" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                服务保障
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                我们承诺为您提供最优质的购物体验
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '正品保障', desc: '所有产品均为品牌方授权或正规渠道进口，假一赔十。' },
                { icon: 'ri-truck-line', title: '冷链配送', desc: '专业冷链包装，全国30+城市极速送达，确保产品新鲜。' },
                { icon: 'ri-refund-2-line', title: '无忧售后', desc: '质量问题24小时内响应，无条件退换货，购物无后顾之忧。' },
                { icon: 'ri-customer-service-2-line', title: '专属客服', desc: '专业顾问1对1服务，帮您选择最适合的黄油产品。' },
              ].map((item) => (
                <div key={item.title} className="text-center p-5">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1.5">{item.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '在线黄油 - 关于我们',
            description: '在线黄油品牌介绍、发展历程、用户评价和联系方式。正品黄油在线选购平台，精选全球优质黄油。',
            url: 'https://dairio.github.io/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '在线黄油',
              url: 'https://dairio.github.io',
            },
          }),
        }}
      />
    </>
  );
}